import type {
  IEnumValue,
  IOptionItem,
  IOrder,
  ISuccessApiResponse,
} from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  serialize,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig, useI18n } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  IDistinctOrderSupplierResponse,
  IPurchaseErrorResponse,
} from '~/purchase/types';
import { ORDER_STATUS_LABEL_LOOKUP } from '~/purchase/translators';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IOrder>, IPurchaseErrorResponse>
  > & {
    args: {
      id: string;
      customerId: string;
    };
  };
  getDistinctSuppliers?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IDistinctOrderSupplierResponse>>,
      IPurchaseErrorResponse
    >
  > & {
    args: {
      customerId: string;
      includeCustomerChildren?: boolean;
    };
  };
  getDistinctStatuses?: OverridableQuery<
    UseQueryOptions<Array<IOptionItem<number>>, IPurchaseErrorResponse>
  > & {
    args: {
      customerId: string;
    };
  };
  countUnsynchronized?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Record<string, number>>,
      IPurchaseErrorResponse
    >
  > & {
    args: {
      customerId: string;
    };
  };
};

export const useManageOrders = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi } = useApi();
  const { t } = useI18n();
  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.orders.detail(options.get?.args.id as string),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IOrder>>(
        `/offers/api/tenants/${tenantCode}/customers/${options.get?.args.customerId}/orders/${
          options.get?.args.id as string
        }`,
      ),
    refetchOnWindowFocus: false,
    retryOnMount: true,
    gcTime: 0,
    staleTime: 0,
    ...options.get,
    enabled:
      Boolean(options.get?.args.id) &&
      Boolean(options.get?.args.customerId) &&
      options.get?.enabled,
  });

  const getDistinctSuppliersQuery = useQuery({
    queryKey: PurchaseQueryKeys.orders.distinctSuppliers(
      tenantCode,
      options.getDistinctSuppliers?.args.customerId as string,
      options.getDistinctSuppliers?.args.includeCustomerChildren as boolean,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IDistinctOrderSupplierResponse>>>(
        `/offers/api/tenants/${tenantCode}/customers/${options.getDistinctSuppliers?.args.customerId}/orders/suppliers?${serialize(
          {
            includeCustomerChildren: options.getDistinctSuppliers?.args
              .includeCustomerChildren
              ? 1
              : 0,
          },
        )}`,
      ),
    refetchOnWindowFocus: false,
    ...options.getDistinctSuppliers,
    enabled:
      Boolean(tenantCode) &&
      Boolean(options.getDistinctSuppliers?.args.customerId) &&
      options.getDistinctSuppliers?.enabled,
  });

  const getDistinctStatusesQuery = useQuery({
    queryKey: PurchaseQueryKeys.orders.distinctStatuses(
      tenantCode,
      options.getDistinctStatuses?.args.customerId as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IEnumValue<number>>>>(
        `/offers/api/tenants/${tenantCode}/customers/${options.getDistinctStatuses?.args.customerId}/orders/statuses`,
      ).then((data) =>
        data.data
          .filter((type) => ORDER_STATUS_LABEL_LOOKUP[type.value])
          .map((type) => ({
            value: type.value,
            label: t(ORDER_STATUS_LABEL_LOOKUP[type.value]),
          })),
      ),
    refetchOnWindowFocus: false,
    ...options.getDistinctStatuses,
    enabled:
      Boolean(tenantCode) &&
      Boolean(options.getDistinctStatuses?.args.customerId) &&
      options.getDistinctStatuses?.enabled,
  });

  const countUnsynchronizedQuery = useQuery({
    queryKey: PurchaseQueryKeys.orders.countUnsynchronized(
      options.countUnsynchronized?.args.customerId as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Record<string, number>>>(
        `/offers/api/tenants/${tenantCode}/customers/${options.countUnsynchronized?.args.customerId}/orders/unsynchronized/count`,
      ),
    refetchOnWindowFocus: false,
    ...options.countUnsynchronized,
    enabled:
      Boolean(tenantCode) &&
      Boolean(options.countUnsynchronized?.args.customerId) &&
      options.countUnsynchronized?.enabled === true,
  });

  return restrictApi(options, {
    get: getQuery,
    getDistinctSuppliers: getDistinctSuppliersQuery,
    getDistinctStatuses: getDistinctStatusesQuery,
    countUnsynchronized: countUnsynchronizedQuery,
  });
};
